// create-indent.component.ts
import { Component, HostListener, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

import { ReactiveFormsModule } from '@angular/forms';
import { IndentListService } from '../indent-list.service';
import { ToastrService } from 'ngx-toastr';
import { InventoryService } from '../../inventory/inventory.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductsFilterComponent } from '../../Common/products-filter/products-filter.component';
import { Router } from '@angular/router';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { AuthService } from '../../../auth/auth.service';
import { FormDraftStore } from '../../../shared/utils/form-draft-store';

/** What the browser safety copy of an unsaved indent holds: `indentForm.getRawValue()`. */
interface IndentDraftSnapshot {
  stores: Array<{ storeId: string; storeName: string; items: any[] }>;
}

@Component({
  selector: 'app-create-indent',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './create-indent.component.html',
  styleUrls: ['./create-indent.component.scss'],
})
export class CreateIndentComponent implements OnInit, OnDestroy {
  indentForm!: FormGroup;
  isSubmitting = false;
  locations: any[] = [];

  private modalService = inject(NgbModal);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private indentService = inject(IndentListService);
  private toastr = inject(ToastrService);
  private locationService = inject(InventoryService);
  private confirmDialog = inject(ConfirmDialogService);
  private zone = inject(NgZone);
  private authService = inject(AuthService);

  ngOnInit(): void {
    this.initForm();
    this.getAllStores();
    this.initDraft();
    // After the first render: the restore question is a modal.
    setTimeout(() => this.offerDraftRestore());
  }

  ngOnDestroy(): void {
    if (this.draftTimer) clearInterval(this.draftTimer);
    this.autosaveDraft();
  }

  private initForm() {
    this.indentForm = this.fb.group({
      stores: this.fb.array([]) // will contain FormGroups: { storeId, storeName, items: FormArray[...] }
    });
  }

  get storesFA(): FormArray {
    return this.indentForm.get('stores') as FormArray;
  }

  private createStoreGroup(storeId: string, storeName: string, items: any[]): FormGroup {
    const itemsFA = this.fb.array(
      items.map(item =>
        this.fb.group({
          variantId: [item.variantId],
          productId: [item.productId],
          sku: [item.sku],
          name: [item.name],
          qty: [item.qty, [Validators.required, Validators.min(0)]],
          uom: [item.uom],
          mrp: [item.mrp],
          sellingPrice: [item.sellingPrice]
        })
      )
    );

    return this.fb.group({
      storeId: [storeId],
      storeName: [storeName],
      items: itemsFA
    });
  }

  private clearStoresFormArray() {
    while (this.storesFA.length) {
      this.storesFA.removeAt(0);
    }
  }

  getAllStores() {
    this.locationService.getAllStores().subscribe({
      next: (data: any) => {
        this.locations = data || [];
      },
      error: () => {
        this.toastr.error('Failed to load stores');
      },
    });
  }

  openFilter() {
    const modalRef = this.modalService.open(ProductsFilterComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
      centered: true
    });

    modalRef.result
      .then((res) => {
        if (res) {
          this.indentService.getFilteredIndentList(res).subscribe({
            next: (data: any) => {
              this.populateStoresFromProducts(data?.items || []);
              this.toastr.success('Filter applied successfully', 'Success');
            },
            error: (err) => {
              console.error('Error applying filter:', err);
              this.toastr.error('Failed to apply filter', 'Error');
            },
          });
        }
      })
      .catch(() => {});
  }

  populateStoresFromProducts(items: any[]) {
    const storeMap = new Map<string, any>();

    for (const variant of items) {
      const variantBase = {
        variantId: variant.variantId,
        productId: variant.productId,
        sku: variant.sku,
        name: variant.name,
        uom: variant.uom,
        mrp: variant.mrp,
        sellingPrice: variant.sellingPrice
      };

      if (Array.isArray(variant.stores)) {
        for (const s of variant.stores) {
          const sid = s.storeId;
          const qty = s.qty ?? 0;
          if (!storeMap.has(sid)) {
            const storeInfo = this.locations.find(l => l.locationId === sid) || this.locations.find(l => l.id === sid) || null;
            const storeName = storeInfo ? (storeInfo.name || storeInfo.storeName || storeInfo.locationName) : sid;
            storeMap.set(sid, {
              storeId: sid,
              storeName,
              items: []
            });
          }
          storeMap.get(sid).items.push({ ...variantBase, qty });
        }
      }
    }

    this.clearStoresFormArray();

    for (const [storeId, storeData] of storeMap.entries()) {
      this.storesFA.push(this.createStoreGroup(storeId, storeData.storeName, storeData.items));
    }
  }

  itemsFA(storeIndex: number): FormArray {
    return this.storesFA.at(storeIndex).get('items') as FormArray;
  }

  // <-- fixed: cast AbstractControl to FormGroup inside map so TS knows controls/value shape -->
  buildRequestBody() {
    const storesPayload = this.storesFA.controls.map((ctrl) => {
      const storeGroup = ctrl as FormGroup;
      const sg = storeGroup.value as {
        storeId: string;
        storeName?: string;
        items: Array<{ variantId: string; qty: number }>;
      };

      return {
        storeId: sg.storeId,
        items: (sg.items || []).map(it => ({
          variantId: it.variantId,
          qty: Number(it.qty) || 0
        }))
      };
    });

    return {
        stores: storesPayload
    };
  }

  async onSubmit() {
    if (this.indentForm.invalid) {
      this.toastr.error('Please fix validation errors before submitting');
      this.indentForm.markAllAsTouched();
      return;
    }

    const body = this.buildRequestBody();

    const storeCount = body.stores.length;
    const itemCount = body.stores.reduce(
      (sum, s) => sum + (s.items?.length || 0),
      0,
    );
    const proceed = await this.confirmDialog.confirm({
      title: 'Submit Indent?',
      message: `Are you sure you want to create indents for <strong>${storeCount}</strong> store(s) covering <strong>${itemCount}</strong> item(s)? Once submitted they are available for PO raising.`,
      confirmText: 'Submit',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    this.indentService.createIndent(body).subscribe({
      next: (res) => {
        this.discardDraft();
        this.toastr.success('Indent created successfully', 'Success');
        this.router.navigate(['/indent-list']);
        this.indentForm.reset();
        this.clearStoresFormArray();
      },
      error: (err) => {
        console.error('Error creating indent:', err);
        this.toastr.error('Failed to create indent', 'Error');
      },
    });
  }

  // add inside CreateIndentComponent class
// add inside CreateIndentComponent class
get stores(): FormArray {
  return this.indentForm.get('stores') as FormArray;
}

getItems(storeIndex: number): FormArray {
  return this.stores.at(storeIndex).get('items') as FormArray;
}

  // ── Unsaved-work safety copy ────────────────────────────────────────────
  // Same protection as GRN: the store-wise quantities live only in the form
  // until Submit, so a session timeout or closed tab would lose them. The
  // form copies itself to the browser every few seconds (FormDraftStore) and
  // offers the copy back when it is opened again.
  private draftStore: FormDraftStore<IndentDraftSnapshot> | null = null;
  private draftTimer: ReturnType<typeof setInterval> | null = null;
  /** False until the restore question is settled, so the still-blank form
   *  cannot overwrite the copy it is about to offer. */
  private draftReady = false;
  /** Submitted to the server, or discarded on purpose: stop copying. */
  private draftClosed = false;

  private initDraft(): void {
    this.draftStore = new FormDraftStore<IndentDraftSnapshot>([
      'indent',
      this.authService.getUserId(),
      this.authService.getActiveLocationId(),
      'new',
    ]);
    // Outside Angular: the timer must not trigger change detection.
    this.zone.runOutsideAngular(() => {
      this.draftTimer = setInterval(() => this.autosaveDraft(), 5000);
    });
  }

  private draftLineCount(d: Partial<IndentDraftSnapshot> | null | undefined): number {
    return (d?.stores || []).reduce((sum, s) => sum + (s?.items?.length || 0), 0);
  }

  /** Also on pagehide: a closed tab or browser keeps the last few seconds. */
  @HostListener('window:pagehide')
  autosaveDraft(): void {
    if (!this.draftStore || !this.draftReady || this.draftClosed) return;
    const snapshot = this.indentForm.getRawValue() as IndentDraftSnapshot;
    // An emptied form leaves any earlier copy alone ("Not now" kept it).
    if (this.draftLineCount(snapshot) > 0) this.draftStore.save(snapshot);
  }

  private discardDraft(): void {
    this.draftClosed = true;
    this.draftStore?.clear();
  }

  /** Offer a copy left by an earlier visit. */
  private async offerDraftRestore(): Promise<void> {
    const stored = this.draftStore?.load();
    const lines = this.draftLineCount(stored?.data);
    if (!stored || lines === 0) {
      this.draftReady = true;
      return;
    }

    const when = new Date(stored.savedAt).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const storeCount = stored.data.stores.length;
    const choice = await this.confirmDialog.choose({
      title: 'Restore unsaved indent?',
      message:
        `This indent was not submitted. A copy from ${when} is still on this computer: ` +
        `<strong>${storeCount}</strong> store${storeCount === 1 ? '' : 's'}, ` +
        `<strong>${lines}</strong> line${lines === 1 ? '' : 's'}.` +
        '<br><small class="text-muted">"Not now" keeps the copy, but it is replaced once you load products here.</small>',
      confirmText: 'Restore',
      altText: 'Discard',
      cancelText: 'Not now',
      size: 'lg',
    });
    if (choice === 'confirm') this.applyDraft(stored.data);
    else if (choice === 'alt') this.draftStore!.clear();
    this.draftReady = true;
  }

  private applyDraft(d: IndentDraftSnapshot): void {
    this.clearStoresFormArray();
    for (const s of d.stores || []) {
      this.storesFA.push(this.createStoreGroup(s.storeId, s.storeName, s.items || []));
    }
    this.toastr.success(
      `${this.draftLineCount(d)} line(s) restored. Submit to create the indents.`,
      'Indent restored',
    );
  }
}
